import { $, $$, setHTML, icons } from './helpers';
import { api } from './api';
import { toast, closeModal, setBreadcrumb } from './ui';
import { openCommandPalette, closeCommandPalette } from './cmd-palette';
import { renderDashboard } from './pages/dashboard';
import { renderClients, renderClientDetail } from './pages/clients';
import { renderProjects, renderProjectDetail } from './pages/projects';
import { renderInvoices, renderInvoiceDetail } from './pages/invoices';
import { renderPayments } from './pages/payments';
import { renderDocuments, renderActivity, renderTaxReport, renderSettings } from './pages/other';
import { renderTickets, renderTicketDetail } from './pages/tickets';
import { renderInbox } from './pages/inbox';
import { renderCompliance } from './pages/compliance';
import { renderFinance } from './pages/finance';

// ─── State ──────────────────────────────────────────────────────────────────
export const state: { token: string | null; user: any } = {
  token: localStorage.getItem('gl_token'),
  user: JSON.parse(localStorage.getItem('gl_user') || 'null'),
};

export function isAdmin(): boolean {
  return state.user?.role === 'admin';
}

// ─── Auth ───────────────────────────────────────────────────────────────────
function login(token: string, user: any) {
  state.token = token;
  state.user = user;
  localStorage.setItem('gl_token', token);
  localStorage.setItem('gl_user', JSON.stringify(user));
  showPanel();
}

export function logout() {
  state.token = null;
  state.user = null;
  localStorage.removeItem('gl_token');
  localStorage.removeItem('gl_user');
  showLogin();
}

function hideSplash() {
  const splash = $('#splash-screen');
  if (splash) (splash as HTMLElement).style.display = 'none';
}

function hideAllScreens() {
  hideSplash();
  ($('#login-screen') as HTMLElement).style.display = 'none';
  ($('#register-screen') as HTMLElement).style.display = 'none';
  ($('#pending-screen') as HTMLElement).style.display = 'none';
  ($('#setup-screen') as HTMLElement).style.display = 'none';
  ($('#forgot-password-screen') as HTMLElement).style.display = 'none';
  ($('#reset-password-screen') as HTMLElement).style.display = 'none';
  ($('#verify-email-screen') as HTMLElement).style.display = 'none';
  ($('#email-verified-screen') as HTMLElement).style.display = 'none';
  ($('#email-failed-screen') as HTMLElement).style.display = 'none';
  ($('#panel') as HTMLElement).style.display = 'none';
}

function showForgotPassword() {
  hideAllScreens();
  ($('#forgot-password-screen') as HTMLElement).style.display = 'flex';
}

function showResetPassword() {
  hideAllScreens();
  ($('#reset-password-screen') as HTMLElement).style.display = 'flex';
}

function showLogin() {
  hideAllScreens();
  ($('#login-screen') as HTMLElement).style.display = 'flex';
}

function showRegister() {
  hideAllScreens();
  ($('#register-screen') as HTMLElement).style.display = 'flex';
}

function showPending() {
  hideAllScreens();
  ($('#pending-screen') as HTMLElement).style.display = 'flex';
  icons();
}

function showSetup() {
  hideAllScreens();
  ($('#setup-screen') as HTMLElement).style.display = 'flex';
}

function showVerifyEmail() {
  hideAllScreens();
  ($('#verify-email-screen') as HTMLElement).style.display = 'flex';
  const emailEl = $('#verify-email-address');
  if (emailEl && state.user) emailEl.textContent = state.user.email || '';
  const resendBtn = $('#resend-verification-btn') as HTMLButtonElement;
  const statusEl = $('#resend-status') as HTMLElement;
  resendBtn.onclick = async () => {
    resendBtn.disabled = true;
    resendBtn.textContent = 'Sending...';
    statusEl.textContent = '';
    try {
      const data = await api.post('/auth/resend-verification', {});
      statusEl.style.color = 'var(--success, #22c55e)';
      statusEl.textContent = data.message || 'Verification email sent. Check your inbox.';
    } catch (err: any) {
      statusEl.style.color = 'var(--danger, #ef4444)';
      statusEl.textContent = err.message || 'Failed to resend. Please try again later.';
    } finally {
      resendBtn.disabled = false;
      resendBtn.textContent = 'Resend Verification Email';
    }
  };
  const logoutBtn = $('#verify-logout-btn');
  if (logoutBtn) logoutBtn.onclick = () => logout();
  icons();
}

function showEmailVerified() {
  hideAllScreens();
  ($('#email-verified-screen') as HTMLElement).style.display = 'flex';
  const loginBtn = $('#email-verified-login-btn');
  if (loginBtn) {
    loginBtn.addEventListener('click', (e) => {
      e.preventDefault();
      history.pushState(null, '', '/');
      showLogin();
    });
  }
  icons();
}

function showEmailFailed() {
  hideAllScreens();
  ($('#email-failed-screen') as HTMLElement).style.display = 'flex';
  const params = new URLSearchParams(window.location.search);
  const reason = params.get('reason');
  const msgEl = $('#email-failed-message') as HTMLElement;
  if (reason === 'expired') {
    msgEl.textContent = 'The verification link has expired. Please sign in and request a new one.';
  } else if (reason === 'rate-limited') {
    msgEl.textContent = 'Too many attempts. Please try again later.';
  } else {
    msgEl.textContent = 'The verification link is invalid or has expired. Please request a new one.';
  }
  const loginBtn = $('#email-failed-login-btn');
  if (loginBtn) {
    loginBtn.addEventListener('click', (e) => {
      e.preventDefault();
      history.pushState(null, '', '/');
      showLogin();
    });
  }
  icons();
}

function showPanel() {
  hideAllScreens();
  ($('#panel') as HTMLElement).style.display = 'flex';
  $('#user-name')!.textContent = state.user.name;
  $('#user-role')!.textContent = state.user.role;
  $('#user-avatar')!.textContent = state.user.name.charAt(0).toUpperCase();
  const headerAvatar = $('#header-user-avatar');
  if (headerAvatar) headerAvatar.textContent = state.user.name.charAt(0).toUpperCase();

  if (isAdmin()) {
    ($('#nav-admin') as HTMLElement).style.display = 'block';
    ($('#nav-admin-reports') as HTMLElement).style.display = 'block';
  } else {
    ($('#nav-admin') as HTMLElement).style.display = 'none';
    ($('#nav-admin-reports') as HTMLElement).style.display = 'none';
  }
  icons();
  navigate(location.pathname || '/dashboard');
}

// ─── Login Form ─────────────────────────────────────────────────────────────
$('#login-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const email = ($('#login-email') as HTMLInputElement).value;
  const password = ($('#login-password') as HTMLInputElement).value;
  const btn = $('#login-btn') as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = 'Signing in...';
  try {
    const data = await api.post('/auth/login', { email, password });
    login(data.token, data.user);
  } catch (err: any) {
    const errEl = $('#login-error') as HTMLElement;
    errEl.textContent = err.message;
    errEl.style.display = 'block';
  } finally {
    btn.disabled = false;
    btn.textContent = 'Sign In';
  }
});

// ─── Setup Form ─────────────────────────────────────────────────────────────
$('#setup-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const btn = e.target instanceof HTMLFormElement ? e.target.querySelector('button[type="submit"]') as HTMLButtonElement : null;
  if (btn) { btn.disabled = true; btn.textContent = 'Creating...'; }
  try {
    await api.post('/setup', {
      name: ($('#setup-name') as HTMLInputElement).value,
      email: ($('#setup-email') as HTMLInputElement).value,
      password: ($('#setup-password') as HTMLInputElement).value,
    });
    toast('Admin account created. Please sign in.', 'success');
    showLogin();
    ($('#login-email') as HTMLInputElement).value = ($('#setup-email') as HTMLInputElement).value;
  } catch (err: any) {
    const errEl = $('#setup-error') as HTMLElement;
    errEl.textContent = err.message;
    errEl.style.display = 'block';
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Create Admin Account'; }
  }
});

// ─── Registration Form ──────────────────────────────────────────────────────
$('#show-register')?.addEventListener('click', (e) => { e.preventDefault(); showRegister(); });
$('#show-login')?.addEventListener('click', (e) => { e.preventDefault(); showLogin(); });
$('#pending-back-to-login')?.addEventListener('click', () => showLogin());

// Account type toggle
$$('.account-type-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    $$('.account-type-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    const type = btn.getAttribute('data-type')!;
    ($('#reg-account-type') as HTMLInputElement).value = type;
    ($('#business-fields') as HTMLElement).style.display = type === 'business' ? 'block' : 'none';
    ($('#individual-vat-section') as HTMLElement).style.display = type === 'individual' ? 'block' : 'none';
    // Toggle required
    const companyInput = $('#reg-company') as HTMLInputElement;
    const vatInput = $('#reg-vat') as HTMLInputElement;
    if (type === 'business') {
      companyInput.required = true;
      vatInput.required = true;
    } else {
      companyInput.required = false;
      vatInput.required = false;
    }
  });
});

// Individual VAT checkbox
$('#reg-has-vat')?.addEventListener('change', (e) => {
  const checked = (e.target as HTMLInputElement).checked;
  ($('#individual-vat-field') as HTMLElement).style.display = checked ? 'block' : 'none';
});

// Country-based VAT hint
$('#reg-country')?.addEventListener('change', (e) => {
  const country = (e.target as HTMLSelectElement).value;
  const vatInput = $('#reg-vat') as HTMLInputElement;
  const indVatInput = $('#reg-individual-vat') as HTMLInputElement;
  const vatPrefixes: Record<string, string> = {
    DE: 'DE', FR: 'FR', NL: 'NL', BE: 'BE', AT: 'ATU', IT: 'IT', ES: 'ES',
    PT: 'PT', IE: 'IE', SE: 'SE', DK: 'DK', FI: 'FI', PL: 'PL', CZ: 'CZ',
    RO: 'RO', BG: 'BG', HR: 'HR', GR: 'EL', HU: 'HU', SK: 'SK', SI: 'SI',
    LT: 'LT', LV: 'LV', EE: 'EE', LU: 'LU', MT: 'MT', CY: 'CY',
    GB: 'GB', CH: 'CHE', TR: 'TR',
  };
  const prefix = vatPrefixes[country] || '';
  const placeholder = prefix ? `e.g. ${prefix}123456789` : 'VAT Number';
  if (vatInput) vatInput.placeholder = placeholder;
  if (indVatInput) indVatInput.placeholder = placeholder;
});

// No manual style init needed — CSS handles .account-type-btn.active

$('#register-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const btn = $('#register-btn') as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = 'Creating account...';
  try {
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = Object.fromEntries(fd);
    // Handle individual VAT
    if (body.account_type === 'individual' && body.has_vat) {
      body.vat_number = ($('#reg-individual-vat') as HTMLInputElement).value;
      body.has_vat = true;
    } else if (body.account_type === 'individual') {
      delete body.vat_number;
      body.has_vat = false;
    }
    delete body.has_vat; // API handles this as boolean
    const data = await api.post('/auth/register', body);
    if (data.pending) {
      showPending();
    } else {
      login(data.token, data.user);
      toast('Welcome to Gravant Labs!', 'success');
    }
  } catch (err: any) {
    const errEl = $('#register-error') as HTMLElement;
    errEl.textContent = err.message;
    errEl.style.display = 'block';
  } finally {
    btn.disabled = false;
    btn.textContent = 'Create Account';
  }
});

// ─── Forgot Password ────────────────────────────────────────────────────────
$('#show-forgot-password')?.addEventListener('click', (e) => { e.preventDefault(); showForgotPassword(); });
$('#forgot-back-to-login')?.addEventListener('click', (e) => { e.preventDefault(); showLogin(); });
$('#reset-back-to-login')?.addEventListener('click', (e) => { e.preventDefault(); showLogin(); });

$('#forgot-password-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const email = ($('#forgot-email') as HTMLInputElement).value;
  const btn = $('#forgot-btn') as HTMLButtonElement;
  const errEl = $('#forgot-error') as HTMLElement;
  const successEl = $('#forgot-success') as HTMLElement;
  errEl.style.display = 'none';
  successEl.style.display = 'none';
  btn.disabled = true;
  btn.textContent = 'Sending...';
  try {
    const data = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }).then(r => r.json());
    successEl.textContent = data.message || 'If an account with that email exists, a reset link has been sent.';
    successEl.style.display = 'block';
  } catch (err: any) {
    errEl.textContent = err.message || 'Something went wrong. Please try again.';
    errEl.style.display = 'block';
  } finally {
    btn.disabled = false;
    btn.textContent = 'Send Reset Link';
  }
});

$('#reset-password-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const password = ($('#reset-password') as HTMLInputElement).value;
  const confirm = ($('#reset-password-confirm') as HTMLInputElement).value;
  const btn = $('#reset-btn') as HTMLButtonElement;
  const errEl = $('#reset-error') as HTMLElement;
  const successEl = $('#reset-success') as HTMLElement;
  errEl.style.display = 'none';
  successEl.style.display = 'none';
  if (password !== confirm) {
    errEl.textContent = 'Passwords do not match.';
    errEl.style.display = 'block';
    return;
  }
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (!token) {
    errEl.textContent = 'Invalid reset link. Please request a new one.';
    errEl.style.display = 'block';
    return;
  }
  btn.disabled = true;
  btn.textContent = 'Resetting...';
  try {
    const res = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      errEl.textContent = data.error || 'Something went wrong.';
      errEl.style.display = 'block';
    } else {
      successEl.textContent = data.message || 'Password has been reset. You can now sign in.';
      successEl.style.display = 'block';
      setTimeout(() => showLogin(), 3000);
    }
  } catch (err: any) {
    errEl.textContent = err.message || 'Something went wrong. Please try again.';
    errEl.style.display = 'block';
  } finally {
    btn.disabled = false;
    btn.textContent = 'Set New Password';
  }
});

// ─── Logout Buttons ─────────────────────────────────────────────────────────
$('#dropdown-logout')?.addEventListener('click', logout);
$('#mobile-logout')?.addEventListener('click', logout);
$('#sidebar-logout')?.addEventListener('click', logout);
function setDrawer(open: boolean) {
  $('#sidebar')?.classList.toggle('open', open);
  $('#sidebar-backdrop')?.classList.toggle('open', open);
  document.body.classList.toggle('drawer-open', open);
}
$('#menu-toggle')?.addEventListener('click', () => {
  setDrawer(!$('#sidebar')?.classList.contains('open'));
});
$('#sidebar-backdrop')?.addEventListener('click', () => setDrawer(false));

// User dropdown toggle
$('#header-user-btn')?.addEventListener('click', () => {
  const dd = $('#header-dropdown') as HTMLElement;
  dd.style.display = dd.style.display === 'none' ? 'block' : 'none';
});
$('#sidebar-user-trigger')?.addEventListener('click', () => {
  navigate('/settings');
});
// Close dropdown on outside click
document.addEventListener('click', (e) => {
  const wrapper = $('#header-user-dropdown');
  if (wrapper && !wrapper.contains(e.target as Node)) {
    ($('#header-dropdown') as HTMLElement).style.display = 'none';
  }
});

// ─── Router ─────────────────────────────────────────────────────────────────
const routes: Record<string, (params: any) => void> = {};

function route(path: string, handler: (params: any) => void) {
  routes[path] = handler;
}

export function navigate(path: string) {
  // Normalize: strip hash prefix if present, default to /dashboard
  if (path.startsWith('#')) path = path.slice(1);
  if (!path || path === '/') path = '/dashboard';

  // Push to browser history (clean URLs)
  if (location.pathname !== path) {
    history.pushState(null, '', path);
  }

  const content = $('#content')!;

  $$('.nav-item').forEach(item => {
    const href = item.getAttribute('href');
    item.classList.toggle('active', href === path || (!!href && path.startsWith(href)));
  });

  setDrawer(false);

  let matched = false;
  for (const pattern in routes) {
    const regex = new RegExp('^' + pattern.replace(/:(\w+)/g, '(?<$1>[^/]+)') + '$');
    const match = path.match(regex);
    if (match) {
      setHTML(content, '<div class="content-loading"><div class="spinner"></div></div>');
      routes[pattern](match.groups || {});
      matched = true;
      break;
    }
  }
  if (!matched) {
    setHTML(content, '<div class="empty-state"><p>Page not found</p><a href="/dashboard" class="btn btn-primary">Go to Dashboard</a></div>');
  }
}

// Back/forward browser buttons
window.addEventListener('popstate', () => navigate(location.pathname));

// Intercept all internal link clicks for SPA navigation
document.addEventListener('click', (e) => {
  const link = (e.target as Element).closest('a[href^="/"]');
  if (link) {
    const href = link.getAttribute('href');
    if (href && href.startsWith('/') && !href.startsWith('/api')) {
      e.preventDefault();
      navigate(href);
    }
  }
});

// ─── Register Routes ────────────────────────────────────────────────────────
route('/dashboard', renderDashboard);
route('/clients', renderClients);
route('/clients/:id', (p: any) => renderClientDetail(parseInt(p.id)));
route('/projects', renderProjects);
route('/projects/:id', (p: any) => renderProjectDetail(parseInt(p.id)));
route('/invoices', renderInvoices);
route('/invoices/:id', (p: any) => renderInvoiceDetail(parseInt(p.id)));
route('/payments', renderPayments);
route('/documents', renderDocuments);
route('/tickets', renderTickets);
route('/tickets/:id', (p: any) => renderTicketDetail(parseInt(p.id)));
route('/inbox', renderInbox);
route('/activity', renderActivity);
route('/tax-report', renderTaxReport);
route('/finance', renderFinance);
route('/compliance', renderCompliance);
route('/settings', renderSettings);

// ─── Global Keyboard Shortcuts ──────────────────────────────────────────────
document.addEventListener('keydown', (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    openCommandPalette();
  }
  if (e.key === 'Escape') {
    closeModal();
    closeCommandPalette();
    ($('#header-dropdown') as HTMLElement).style.display = 'none';
  }
});

// Cmd+K trigger button
$('#cmd-k-trigger')?.addEventListener('click', openCommandPalette);

// ─── Init ───────────────────────────────────────────────────────────────────
async function init() {
  // Handle password reset URL
  if (location.pathname === '/reset-password' && new URLSearchParams(location.search).get('token')) {
    hideSplash();
    showResetPassword();
    return;
  }
  // Handle email verified success page
  if (location.pathname === '/email-verified') {
    hideSplash();
    showEmailVerified();
    return;
  }
  // Handle email verification failed page
  if (location.pathname === '/email-failed') {
    hideSplash();
    showEmailFailed();
    return;
  }
  if (state.token) {
    try {
      const data = await api.get('/auth/me');
      state.user = data.user;
      localStorage.setItem('gl_user', JSON.stringify(data.user));
      if (state.user && !state.user.email_verified) {
        showVerifyEmail();
        return;
      }
      showPanel();
    } catch {
      checkSetup();
    }
  } else {
    checkSetup();
  }
}

async function checkSetup() {
  try {
    const res = await fetch('/api/setup/status');
    const data = await res.json();
    if (data.needs_setup) {
      showSetup();
    } else {
      showLogin();
    }
  } catch {
    showLogin();
  }
}

init();
